import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   *
   * @type {{ x: number, y: number }}
   */
  #lastPointer = {
    x: 0,
    y: 0,
  };

  /**
   * Update the safety-box idle state on the active menu item.
   *
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;

    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check whether the pointer is over a different menu item and trigger
   * activation when necessary.
   *
   * This works around Safari not re-evaluating hit targets after
   * pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);

      if (!target) return;

      const listItem = target.closest('.menu-list__list-item');

      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(
          new PointerEvent('pointerenter', {
            bubbles: false,
          })
        );
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();

    const isOverlap = this.headerComponent?.hasAttribute(
      'data-submenu-overlap-bottom-row'
    );

    const boundary = isOverlap
      ? this.headerComponent?.querySelector('.header__row--top')
      : this.headerComponent;

    const boxHeight =
      (boundary?.getBoundingClientRect().bottom ?? 0) - rect.top;

    item.style.setProperty('--box-height', `${boxHeight}px`);
  }

  /**
   * Stop pointer tracking and remove all safety-box properties from an item.
   *
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);

    this.#pointerIdleTimer = undefined;

    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered.
   *
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * Activate the selected menu item.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) {
      return;
    }

    const item = findMenuItem(event.target);

    if (!item) return;

    const isDefaultSlot = event.target.slot === '';

    /*
     * Sticky-header state changes can reposition the header without producing
     * a pointerleave event.
     *
     * In that situation the same item may remain active while its submenu
     * height variables are stale or zero.
     *
     * Refresh the submenu measurement instead of returning immediately.
     */
    if (item === this.#state.activeItem) {
      let activeSubmenu = findSubmenu(item);
      const activeHasSubmenu = Boolean(activeSubmenu);

      if (!activeHasSubmenu && !isDefaultSlot) {
        activeSubmenu = this.overflowMenu;
      }

      if (activeSubmenu) {
        activeSubmenu.dataset.active = '';

        this.#scheduleSubmenuMeasurement(
          activeSubmenu,
          isDefaultSlot,
          activeHasSubmenu
        );
      }

      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);

      this.#hoverDispatchTimer = undefined;

      const committedItem = item;

      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;

          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      /*
       * Mark submenu as active for content-visibility optimization.
       */
      submenu.dataset.active = '';

      /*
       * Cleanup an existing observer from a previous activation.
       */
      this.#cleanupMutationObserver();

      /*
       * Monitor DOM mutations to catch deferred content injection from
       * section hydration.
       */
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#scheduleSubmenuMeasurement(
          submenu,
          isDefaultSlot,
          hasSubmenu,
          true
        );
      });

      this.#submenuMutationObserver.observe(submenu, {
        childList: true,
        subtree: true,
      });

      /*
       * Auto-disconnect to prevent memory leaks.
       */
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    /*
     * Apply an immediate measurement, then measure again after two animation
     * frames so sticky-header geometry and content-visibility have settled.
     */
    this.#applySubmenuMeasurement(
      submenu,
      isDefaultSlot,
      hasSubmenu
    );

    this.#scheduleSubmenuMeasurement(
      submenu,
      isDefaultSlot,
      hasSubmenu
    );

    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item.
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(document.activeElement);

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }

      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately.
   *
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    /*
     * Do not deactivate while the overflow menu remains hovered.
     */
    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    clearTimeout(this.#hoverDispatchTimer);

    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(0, 0);

    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    /*
     * Remove active state from submenu after closing.
     */
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Measure and apply the active submenu height.
   *
   * `content-visibility: auto` may return the intrinsic placeholder height or
   * zero on the first synchronous measurement after `data-active` is added.
   *
   * This happens more frequently after the header changes to its sticky state.
   *
   * Prefer the largest available real measurement and update the header height
   * variables together.
   *
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #applySubmenuMeasurement(
    submenu,
    isDefaultSlot,
    hasSubmenu
  ) {
    let finalHeight = 0;

    if (submenu) {
      finalHeight = Math.max(
        submenu.offsetHeight || 0,
        submenu.scrollHeight || 0,
        Math.ceil(
          submenu.getBoundingClientRect().height || 0
        )
      );
    }

    /*
     * For the overflow menu, the required height is either the submenu content
     * height or the total height of the overflow-list links.
     */
    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight || 0;

        finalHeight = Math.max(
          finalHeight,
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight = Math.max(
          finalHeight,
          overflowListHeight
        );
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      finalHeight > 0 ? '1' : '0'
    );

    return finalHeight;
  }

  /**
   * Re-measure after the browser has applied:
   *
   * - `data-active`
   * - `content-visibility`
   * - sticky-header positioning
   * - submenu layout
   *
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @param {boolean} [cleanupObserver]
   */
  #scheduleSubmenuMeasurement(
    submenu,
    isDefaultSlot,
    hasSubmenu,
    cleanupObserver = false
  ) {
    if (!submenu) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const isStillActive =
          this.#state.activeItem &&
          submenu.dataset.active !== undefined;

        if (isStillActive) {
          this.#applySubmenuMeasurement(
            submenu,
            isDefaultSlot,
            hasSubmenu
          );
        }

        if (cleanupObserver) {
          this.#cleanupMutationObserver();
        }
      });
    });
  }

  /**
   * Get the height of overflow-list links while hiding nested submenus.
   *
   * @returns {number}
   */
  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector('slot')
      ?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    /**
     * @param {(submenu: HTMLElement) => void} callback
     */
    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu =
          /** @type {HTMLElement | null} */ (
            link.querySelector('[ref="submenu[]"]')
          );

        if (submenu) {
          callback(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height =
      this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * Read the visible header height before submenu-height writes invalidate
   * layout.
   *
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    if (
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
    ) {
      const topRow =
        /** @type {HTMLElement | null} */ (
          this.headerComponent.querySelector(
            '.header__row--top'
          )
        );

      return topRow?.offsetHeight ?? 0;
    }

    return this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height.
   *
   * When the submenu is closed, the full open height is zero.
   *
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;

    const fullOpenHeaderHeight = nothingToOpen
      ? 0
      : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload images that are configured to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  /**
   * Disconnect and clear the active submenu MutationObserver.
   */
  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu item associated with an event target.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    /*
     * Select the first overflowing menu item when hovering the More item.
     */
    return findMenuItem(
      element.parentElement?.querySelector(
        '[slot="overflow"]'
      )
    );
  }

  const menuItem = element.querySelector(
    '[ref="menuitem"]'
  );

  return menuItem instanceof HTMLElement
    ? menuItem
    : null;
}

/**
 * Find the submenu related to a menu item.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector(
    '[ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}